import { Link } from '@tanstack/react-router';
import type { ComponentType } from 'react';
import { hasDashboard, type AppMeta } from '../lib/meta.js';
import { GuideSection, GuideSteps, GuideTip, OnScreen } from './guide.js';

/**
 * The core's Help pages: one short guide per screen, written once for every deploy. Each names
 * buttons and headings exactly as the screen shows them, and a guide (or a part of one) shows
 * only to people who have that screen, so nobody reads about something they can't open.
 * A client's own pages come from the `app.help` slot and are listed after these.
 */

export interface CoreGuide {
  id: string;
  title: string;
  summary: string;
  /** Show the guide only when this returns true. */
  when?: (meta: AppMeta) => boolean;
  component: ComponentType<{ meta: AppMeta }>;
}

/** An inline link, as the core's screens draw one. */
const LINK = 'font-medium text-brand-strong hover:underline';

const can = (meta: AppMeta, capability: string) => meta.capabilities.includes(capability);
/** Asking for a change is offered: on the dashboard's cards, in a deploy that isn't read-only. */
const canAsk = (meta: AppMeta) => hasDashboard(meta) && meta.access.writable;

function GettingStarted({ meta }: { meta: AppMeta }) {
  const product = meta.branding.product_name;
  return (
    <>
      <GuideSection title="The menu">
        <p>
          The menu down the left side holds everything you can open. On a phone or a small tablet it
          is folded away: tap <OnScreen>Menu</OnScreen> at the top left to open it.
        </p>
        <p>
          It starts with <OnScreen>Dashboard</OnScreen> (or <OnScreen>Home</OnScreen> before the
          dashboard has data), <OnScreen>Reports</OnScreen>, <OnScreen>Help</OnScreen> and{' '}
          <OnScreen>What’s new</OnScreen>, then your lists grouped under headings such as{' '}
          <OnScreen>Sales</OnScreen> and <OnScreen>Operations</OnScreen>. You only see what your
          role lets you see, so two people can have different menus.
        </p>
        <p>
          <OnScreen>What’s new</OnScreen> lists changes to {product} as they arrive, newest first. A
          dot beside it means there is one you haven’t seen; opening the page clears it.
        </p>
      </GuideSection>
      <GuideSection title="The top of every screen">
        <p>
          The bell shows notifications meant for you. A red number means some are unread;{' '}
          <OnScreen>Mark all read</OnScreen> clears it.
        </p>
        <p>
          The person icon at the top right (with your name beside it on a wider screen) opens your
          account menu: <OnScreen>Appearance</OnScreen> switches between light and dark,{' '}
          <OnScreen>Settings</OnScreen> (or <OnScreen>Your account</OnScreen>) changes your password
          and sign-in, and <OnScreen>Sign out</OnScreen> signs you out.
        </p>
      </GuideSection>
      <GuideSection title="Where to start">
        <GuideSteps>
          {hasDashboard(meta) && (
            <li>
              Open{' '}
              <Link className={LINK} to="/help/$guide" params={{ guide: 'dashboard' }}>
                Reading your dashboard
              </Link>{' '}
              to see what each part of the dashboard tells you.
            </li>
          )}
          <li>
            Open a list from the menu and click a record to see everything about it. The{' '}
            <Link className={LINK} to="/help/$guide" params={{ guide: 'records' }}>
              Lists and records
            </Link>{' '}
            guide covers searching, editing and the tabs on a record.
          </li>
          {canAsk(meta) && (
            <li>
              If something isn’t how you want it, use <OnScreen>Ask for a change</OnScreen> on the
              card it’s about. See{' '}
              <Link className={LINK} to="/help/$guide" params={{ guide: 'ask-for-a-change' }}>
                Asking for a change
              </Link>
              .
            </li>
          )}
        </GuideSteps>
        <GuideTip>
          {product} is built for your business, so it changes as you do. If a word, a number or a
          screen doesn’t fit how you work, say so: that is how it gets better.
        </GuideTip>
      </GuideSection>
    </>
  );
}

function Dashboard({ meta }: { meta: AppMeta }) {
  return (
    <>
      <GuideSection title="Where the numbers come from">
        <p>
          The dashboard reads copies of what your own tools hold, such as your accounting and job
          software. The line of small labels near the top shows each source and when it last synced.{' '}
          <OnScreen>late</OnScreen> or <OnScreen>failing</OnScreen> means the numbers may be behind;
          hovering over the label shows why.
        </p>
        <p>
          The numbers refresh by themselves every few minutes while the page is open. If you have
          more than one view, the tabs under the title switch between them.
        </p>
      </GuideSection>
      <GuideSection title="Reading it from the top">
        <GuideSteps>
          <li>The opening lines sum up the day: what needs you most, in a sentence or two.</li>
          <li>
            <OnScreen>Your number</OnScreen> is the one figure you said you most want to know each
            morning.
          </li>
          <li>The figure cards are your main numbers at a glance.</li>
          <li>
            <OnScreen>Needs you today</OnScreen> lists what is overdue or at risk. Click a row to
            see the records behind it.
          </li>
          <li>
            <OnScreen>Questions, answered</OnScreen> holds the questions you asked for when the
            dashboard was built.
          </li>
          <li>Below those are charts and tables, each with a report you can open in full.</li>
        </GuideSteps>
      </GuideSection>
      <GuideSection title="What’s in a number">
        <p>
          Under a figure, <OnScreen>What’s in this number</OnScreen> shows how it is worked out and
          the records that make it up. Click a record to open it.
        </p>
        {can(meta, 'manage_config') && (
          <p>
            <OnScreen>Make this your number</OnScreen> on a figure card moves it to the top as{' '}
            <OnScreen>Your number</OnScreen>; <OnScreen>Unpin</OnScreen> puts it back.
          </p>
        )}
      </GuideSection>
      {meta.dashboard.sample && (
        <GuideSection title="Sample data">
          <GuideTip>
            While the <OnScreen>Sample data</OnScreen> badge shows at the top of the screen, every
            number is invented for a demonstration. It goes once your own tools are connected.
          </GuideTip>
        </GuideSection>
      )}
    </>
  );
}

function AskForAChange({ meta }: { meta: AppMeta }) {
  const forwarded = meta.dashboard.change_requests.forwarded;
  return (
    <>
      <GuideSection title="How to ask">
        <GuideSteps>
          <li>
            Find the card the change is about on the dashboard, and click{' '}
            <OnScreen>Ask for a change</OnScreen> on it. For the whole view, use the link at the
            bottom of the page.
          </li>
          <li>
            Say what you’d like in your own words, such as “split this by technician” or “show last
            year beside this year”.
          </li>
          <li>
            Click <OnScreen>Send</OnScreen>.
          </li>
        </GuideSteps>
      </GuideSection>
      <GuideSection title="What happens next">
        {forwarded ? (
          <p>
            Your request goes to the team that looks after this system, with your name, your email
            and which card it is about. None of the numbers or records on the card are sent.
          </p>
        ) : (
          <p>Your request is kept here, where your administrators can see it.</p>
        )}
        <p>
          You can see what you asked for, and whether it was sent, in{' '}
          <Link className={LINK} to="/settings">
            Settings
          </Link>{' '}
          under <OnScreen>Changes asked for</OnScreen>.
        </p>
      </GuideSection>
    </>
  );
}

function Records({ meta }: { meta: AppMeta }) {
  // What this person may do to at least one kind of record (none, on a read-only deploy).
  const may = {
    create: meta.entities.some((e) => e.access.create),
    update: meta.entities.some((e) => e.access.update),
    act: meta.entities.some((e) => e.access.actions.length > 0),
    archive: meta.entities.some((e) => e.access.archive),
  };
  return (
    <>
      <GuideSection title="Finding a record">
        <p>
          Open a list from the menu. Type in <OnScreen>Search</OnScreen> to narrow it as you type.
          Where a list has states, <OnScreen>Filter by state</OnScreen> shows only the open, done or
          canceled ones, and <OnScreen>Saved view</OnScreen> switches to a view set up for you.
        </p>
        <p>
          Click a column heading to sort by it; click again to reverse. On a phone, use{' '}
          <OnScreen>Sort by</OnScreen> above the list. Lists show 50 at a time, with{' '}
          <OnScreen>Previous</OnScreen> and <OnScreen>Next</OnScreen> at the bottom.
        </p>
      </GuideSection>
      {(may.create || may.update || may.act || may.archive) && (
        <GuideSection title="Adding and changing">
          <GuideSteps>
            {may.create && (
              <li>
                To add one, click the <OnScreen>New</OnScreen> button at the top of the list (such
                as <OnScreen>New lead</OnScreen>), fill in the form and click its{' '}
                <OnScreen>Create</OnScreen> button. Required fields are marked.
              </li>
            )}
            {may.update && (
              <li>
                To change one, open it and click <OnScreen>Edit</OnScreen>, then{' '}
                <OnScreen>Save changes</OnScreen>.
              </li>
            )}
            {may.act && (
              <li>
                Buttons{' '}
                {may.update ? (
                  <>
                    beside <OnScreen>Edit</OnScreen>
                  </>
                ) : (
                  'at the top of a record'
                )}
                , such as <OnScreen>Mark won</OnScreen>, move a record to its next step. Hover over
                one to see what it does.{' '}
                {may.update && 'Save any changes you are making first: a step uses what is saved.'}
              </li>
            )}
          </GuideSteps>
          {may.update && (
            <GuideTip>
              Some records, such as an invoice once it is issued, can no longer be edited. The
              record says so where its fields are.
            </GuideTip>
          )}
          {may.archive && (
            <p>
              The <OnScreen>…</OnScreen> button at the top right has <OnScreen>Archive</OnScreen>,
              which takes a record out of the way without deleting it, and{' '}
              <OnScreen>Restore</OnScreen> to bring it back.
            </p>
          )}
        </GuideSection>
      )}
      <GuideSection title="The tabs on a record">
        <p>
          <OnScreen>Details</OnScreen> holds its fields. <OnScreen>Related</OnScreen> lists
          everything that points at it, such as a customer’s jobs and invoices.{' '}
          <OnScreen>Comments</OnScreen> is for notes your team can read, and{' '}
          <OnScreen>Files</OnScreen> holds attachments.
        </p>
        <p>
          <OnScreen>History</OnScreen> shows every change: when, who, and what it was before. A
          change made by another tool or by the system says so.
        </p>
      </GuideSection>
    </>
  );
}

function Reports({ meta }: { meta: AppMeta }) {
  return (
    <>
      <GuideSection title="Running a report">
        <p>
          Open <OnScreen>Reports</OnScreen> from the menu and click one. It runs straight away and
          shows only what you are allowed to see.
        </p>
        <p>
          <OnScreen>Download CSV</OnScreen> saves it as a file that opens in Excel or Google Sheets.
          A <OnScreen>Read with care</OnScreen> note means some of the numbers need a second look,
          and says why.
        </p>
      </GuideSection>
      <GuideTip>
        Need a report that isn’t here, or one cut a different way? Ask your administrator
        {canAsk(meta) && (
          <>
            , or use <OnScreen>Ask for a change</OnScreen> on the dashboard
          </>
        )}
        .
      </GuideTip>
    </>
  );
}

function SignIn() {
  return (
    <>
      <GuideSection title="Signing in">
        <p>
          You sign in with your email and password, then a six-digit code from an authenticator app
          on your phone. Everyone uses the code: it keeps your business’s data safe even if a
          password leaks.
        </p>
        <p>
          The first time you sign in, you add your account to an app such as Google Authenticator,
          Microsoft Authenticator or 1Password, by scanning the code on the screen. You then get a
          set of recovery codes.
        </p>
        <GuideTip>
          Keep your recovery codes somewhere safe, such as a password manager. They are shown only
          once.
        </GuideTip>
      </GuideSection>
      <GuideSection title="If you don’t have your phone">
        <GuideSteps>
          <li>
            On the code screen, click <OnScreen>Use a recovery code instead</OnScreen> and type one
            of your recovery codes. Each works once.
          </li>
          <li>
            If you have no codes left, ask an administrator to reset your two-factor sign-in. The
            next time you sign in, you set it up again.
          </li>
        </GuideSteps>
        <p>
          Running low? In{' '}
          <Link className={LINK} to="/settings">
            Settings
          </Link>
          , under <OnScreen>Two-factor sign-in</OnScreen>, type a code from your app and click{' '}
          <OnScreen>New recovery codes</OnScreen>. The old ones stop working.
        </p>
      </GuideSection>
      <GuideSection title="Your password">
        <p>
          Change it in{' '}
          <Link className={LINK} to="/settings">
            Settings
          </Link>
          , under <OnScreen>Password</OnScreen>. It needs at least 12 characters; a few unrelated
          words make a good one. Changing it signs out your other devices.
        </p>
        <p>
          Forgotten it? There is no reset link on the sign-in page, so ask your administrator to
          pass the request to the team that looks after this system.
        </p>
      </GuideSection>
    </>
  );
}

function Settings({ meta }: { meta: AppMeta }) {
  return (
    <>
      <GuideSection title="Your own settings">
        <p>
          Open the account menu (the person icon at the top right), then{' '}
          <OnScreen>Settings</OnScreen> (or <OnScreen>Your account</OnScreen>). Everyone can change
          their password and two-factor sign-in there.
        </p>
        <p>
          <OnScreen>Summary email</OnScreen>, where it shows, chooses whether the top of your
          dashboard comes by email at 6:00 in the morning: on <OnScreen>Mondays</OnScreen>,{' '}
          <OnScreen>Every weekday</OnScreen> or <OnScreen>Never</OnScreen>.
        </p>
        <p>
          <OnScreen>API tokens</OnScreen> let another program use the system as you. Only create one
          when a tool you trust asks for it, and click <OnScreen>Revoke</OnScreen> when it no longer
          needs it.
        </p>
      </GuideSection>
      {can(meta, 'manage_users') && (
        <GuideSection title="People">
          <GuideSteps>
            <li>
              Under <OnScreen>People</OnScreen>, click <OnScreen>Invite</OnScreen>, fill in their
              name and email, tick their roles and click <OnScreen>Send invitation</OnScreen>. The
              emailed link works for seven days.
            </li>
            <li>
              When someone leaves, click <OnScreen>Deactivate</OnScreen> on their row. They can no
              longer sign in, and everything they did stays in the records’ history.
            </li>
            <li>
              If someone loses their phone, click <OnScreen>Reset two-factor</OnScreen>. Do it only
              when you’re sure the request comes from them.
            </li>
          </GuideSteps>
        </GuideSection>
      )}
      {can(meta, 'manage_integrations') && (
        <GuideSection title="Dashboard data">
          <p>
            <OnScreen>Dashboard data</OnScreen> lists the tools the dashboard reads and when each
            last synced. Before your own tools are connected, <OnScreen>Load sample data</OnScreen>{' '}
            fills the dashboard with an invented business so you can see how it works.
          </p>
        </GuideSection>
      )}
      {can(meta, 'export_data') && (
        <GuideSection title="Your data">
          <p>
            Your data is yours. <OnScreen>Export all data</OnScreen> under{' '}
            <OnScreen>Your data</OnScreen> downloads everything as one ZIP file, with a list of what
            each file holds. Copies are also taken on a schedule, weekly unless an administrator
            changed it.
          </p>
        </GuideSection>
      )}
    </>
  );
}

export const CORE_GUIDES: readonly CoreGuide[] = [
  {
    id: 'getting-started',
    title: 'Finding your way around',
    summary: 'The menu, notifications, your account, and where to start.',
    component: GettingStarted,
  },
  {
    id: 'dashboard',
    title: 'Reading your dashboard',
    summary: 'What each part shows, and what’s behind a number.',
    when: hasDashboard,
    component: Dashboard,
  },
  {
    id: 'ask-for-a-change',
    title: 'Asking for a change',
    summary: 'Asking for a number, a card or a view to be different.',
    when: canAsk,
    component: AskForAChange,
  },
  {
    id: 'records',
    title: 'Lists and records',
    summary: 'Finding, adding and changing records, and the tabs on a record.',
    component: Records,
  },
  {
    id: 'reports',
    title: 'Reports',
    summary: 'Running a report and downloading it.',
    when: (meta) => meta.reports.length > 0,
    component: Reports,
  },
  {
    id: 'sign-in',
    title: 'Signing in',
    summary: 'Two-factor sign-in, recovery codes and your password.',
    component: SignIn,
  },
  {
    id: 'settings',
    title: 'Settings',
    summary: 'Your account, and what administrators manage.',
    component: Settings,
  },
];

export function guidesFor(meta: AppMeta): CoreGuide[] {
  return CORE_GUIDES.filter((guide) => guide.when?.(meta) ?? true);
}
